import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import history from 'history/browser';
import queryString from 'query-string';
import { search } from '../search';

export const initialAppState = {
  tabIdentifier: '',
  pageIdentifier: '',
  selectedIndex: 0,
  initialTabId: '',
  lists: [],
};

export const performSearch = createAsyncThunk(
  'search/fetch',
  async (args = {}, { getState }) => {
    const { page = 1, index, resetPages = false, filterQuery = {} } = args;

    const {
      lists,
      selectedIndex,
    } = getState().app;

    let { searchSettings: { endpoint }, query, pages = [], size } = lists[index ?? selectedIndex];

    let from = 0;
    if (page > 1) {
      from = (page - 1) * size;
    }

    if (pages[page > 1 ? from / size : 0] === undefined || page === 1) {
      const response = await search({ url: endpoint, queryParams: { ...query, ...filterQuery, from, size } });
      return { response, from, size, page, resetPages, index: index ?? selectedIndex };
    }
    return { from, size, page, resetPages, index: index ?? selectedIndex };
  },
);

export const getInitalSearchResults = createAsyncThunk(
  'search/init',
  async (args = {}, { getState, dispatch }) => {
    const {
      lists,
      selectedIndex,
    } = getState().app;

    const { filters = [], pages = [] } = lists[selectedIndex];
    const filterQuery = {};
    const hashObject = queryString.parse(history.location.hash);
    for (const k in hashObject) {
      const i = filters.find(({ term }) => term === k);
      if (i) {
        const searchTerms = hashObject[k].split(',');
        for (const searchTerm of searchTerms) {
          const x = i.items.find(({ key }) => key === searchTerm);
          if (x) {

            if (filterQuery[k]) {
              filterQuery[k].push(searchTerm);
            } else {
              filterQuery[k] = [searchTerm];
            }
          }
        }
      }
    }
    if (Object.keys(filterQuery).length > 0) {
      await dispatch(setFilter({ filterQueryOverwrite: filterQuery }));
    } else if (pages.length < 2) {
      await dispatch(performSearch({ index: selectedIndex }));
    }

    lists.map(async (e, index) => {
      if (index !== selectedIndex) {
        await dispatch(performSearch({ index }));
      }
    });
  },
);

export const setFilter = createAsyncThunk(
  'filter/set',
  async (args = {}, { getState, dispatch }) => {
    const { key, term, filterQueryOverwrite } = args;
    const {
      lists,
      selectedIndex,
    } = getState().app;

    if (filterQueryOverwrite) {
      await dispatch(performSearch({ page: 1, index: selectedIndex, filterQuery: { filterQuery: filterQueryOverwrite }, resetPages: true }));
      return { filterQuery: filterQueryOverwrite, selectedIndex };
    } else {
      const { query: { filterQuery = {} } } = lists[selectedIndex];

      let clone = { ...filterQuery };
      if (clone[term]) {
        const i = clone[term].findIndex((e) => e === key);
        if (i > -1) {
          if (clone[term].length === 1) {
            delete clone[term];
          } else {
            clone[term] = [...clone[term].filter((e) => e !== key)];
          }
        } else {
          clone[term] = [...clone[term], key];
        }
      } else {
        clone[term] = [key];
      }

      await dispatch(performSearch({ page: 1, index: selectedIndex, filterQuery: { filterQuery: clone }, resetPages: true }));
      return { filterQuery: clone, selectedIndex };
    }
  },
);

export const appSlice = createSlice({
  name: 'app',
  initialState: initialAppState,
  reducers: {
    setSelectedIndex: (state, { payload }) => {
      state.selectedIndex = payload.index;
    },
    setUrlParams: (state, { payload }) => {
      const currentParams = queryString.parse(history.location.search);
      const { get, remove } = payload;
      const search = [];
      const { lists, selectedIndex } = state;
      const { query: { filterQuery = {} } } = lists[selectedIndex];

      for (const k in get) {
        if (remove) {
          delete currentParams[k];
        } else {
          currentParams[k] = get[k];
        }
      }

      Object.entries(currentParams).map(([k, v]) => {
        search.push(`${k}=${encodeURIComponent(v)}`);
      });

      const hashArray = [];
      for (const k in filterQuery) {
        hashArray.push(
          k + '=' + encodeURIComponent(filterQuery[k].join(',')),
        );
      }

      history.replace({
        search: search.join('&'),
        hash: hashArray.join('&'),
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setFilter.pending, (state) => {
      const { selectedIndex } = state;

      state.lists[selectedIndex]['pages'] = [];
    });
    builder.addCase(performSearch.pending, (state, action) => {
      const { index } = action.meta.arg;

      if (state.lists[index]) {
        state.lists[index]['loading'] = true;
        state.lists[index]['error'] = false;
      }
    });
    builder.addCase(performSearch.rejected, (state, action) => {
      const { index } = action.meta.arg;

      if (state.lists[index]) {
        state.lists[index]['loading'] = false;
        state.lists[index]['error'] = true;
      }
    });
    builder.addCase(performSearch.fulfilled, (state, { payload }) => {
      const { index, response } = payload;

      state.lists[index]['loading'] = false;
      state.lists[index]['error'] = false;

      if (response) {
        const {
          response: {
            aggregations = {},
            hits: {
              hits = [],
              total: { value: total },
            },
          }, from, size, page, resetPages,
        } = payload;

        if (hits && hits.length > 0) {
          const { lists } = state,
            { pages, facets: defaultFacets } = lists[index];

          if (!pages || (page === 1 && resetPages)) {
            state.lists[index]['pages'] = [];
          }
          state.lists[index]['pages'][page > 1 ? from / size : 0] = hits;
          state.lists[index]['maxPages'] = Math.ceil(
            total / size,
          );

          if (
            defaultFacets &&
            defaultFacets.length > 0 &&
            Object.keys(aggregations).length > 0
          ) {
            state.lists[index]['filters'] = [];
            const { facets = {} } = aggregations,
              { name = {} } = facets,
              { buckets } = name,
              { filtersOverwrite = {} } = lists[index];
            if (buckets) {
              defaultFacets.forEach(facet => {
                const { term = '' } = facet;
                buckets.map(bucket => {
                  const { key = '', value = {} } = bucket,
                    { buckets = [] } = value;
                  if (key === term && buckets.length > 0) {
                    lists[index]['filters'].push({
                      label: facet.label,
                      term,
                      items:
                        term in filtersOverwrite ? filtersOverwrite[term] : buckets,
                    });
                  }
                });
              });
            }
          }
        }
      }
    });

    builder.addCase(setFilter.fulfilled, (state, { payload }) => {
      const { filterQuery, selectedIndex } = payload;

      const hashArray = [];
      for (const k in filterQuery) {
        hashArray.push(
          k + '=' + encodeURIComponent(filterQuery[k].join(',')),
        );
      }

      history.replace({
        search: history.location.search,
        hash: hashArray.join('&'),
      });

      state.lists[selectedIndex]['query']['filterQuery'] = filterQuery;
    });
  },
});

export default appSlice.reducer;
export const { setSelectedIndex, setUrlParams } = appSlice.actions;

